import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate } from 'react-router-dom';
import { useAuth, useLogin } from '../hooks/useAuth';
import toast from 'react-hot-toast';

const schema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type FormData = z.infer<typeof schema>;

export default function Login() {
  const { user, isLoading: authLoading } = useAuth();
  const login = useLogin();

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  if (!authLoading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: FormData) => {
    try {
      await login.mutateAsync(data);
    } catch (err: any) {
      toast.error(err.message || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Left panel — dark branding */}
      <div
        className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundColor: '#18181B' }}
      >
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />

        {/* Top: Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg font-extrabold"
            style={{ backgroundColor: '#F97316' }}
          >
            T
          </div>
          <div>
            <p className="text-white font-extrabold text-lg leading-tight tracking-tight">Tkxel</p>
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#F97316' }}>
              SEO Dashboard
            </p>
          </div>
        </div>

        {/* Middle: headline */}
        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold text-white leading-tight tracking-tight mb-4">
            Track every<br />
            <span style={{ color: '#F97316' }}>SEO task</span><br />
            that matters.
          </h2>
          <p className="text-[#71717A] text-base leading-relaxed max-w-xs">
            A unified dashboard to manage sitewide fixes, page-level optimizations, and team workloads — all in one place.
          </p>

          {/* Feature list */}
          <div className="mt-8 space-y-3">
            {[
              'Real-time progress tracking',
              'Team workload at a glance',
              'Weekly plan & Kanban board',
              'Export reports as CSV',
            ].map(item => (
              <div key={item} className="flex items-center gap-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(249,115,22,0.15)' }}
                >
                  <svg className="w-3 h-3" style={{ color: '#F97316' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-[#A1A1AA]">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom: copyright */}
        <div className="relative z-10">
          <p className="text-[#3F3F46] text-xs">© 2025 Tkxel. All rights reserved.</p>
        </div>
      </div>

      {/* Right panel — white login form */}
      <div
        className="flex-1 flex items-center justify-center p-8"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-extrabold"
              style={{ backgroundColor: '#F97316' }}
            >
              T
            </div>
            <div>
              <p className="text-[#18181B] font-extrabold text-base">Tkxel</p>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#F97316' }}>SEO Dashboard</p>
            </div>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-[#18181B] tracking-tight">Welcome back</h1>
            <p className="text-sm text-[#71717A] mt-1">Sign in to your account to continue</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#18181B] mb-1.5">
                Username
              </label>
              <input
                {...register('username')}
                type="text"
                autoComplete="username"
                placeholder="Enter username"
                className="w-full border rounded-lg px-4 py-2.5 text-sm text-[#18181B] placeholder-[#A1A1AA] bg-white transition-all duration-150 focus:outline-none"
                style={{
                  border: errors.username ? '1px solid #DC2626' : '1px solid #E4E4E7',
                }}
                onFocus={e => {
                  e.currentTarget.style.border = '1px solid #F97316';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249,115,22,0.1)';
                }}
                onBlur={e => {
                  e.currentTarget.style.border = errors.username ? '1px solid #DC2626' : '1px solid #E4E4E7';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {errors.username && (
                <p className="mt-1.5 text-xs text-[#DC2626] flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.username.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#18181B] mb-1.5">
                Password
              </label>
              <input
                {...register('password')}
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full border rounded-lg px-4 py-2.5 text-sm text-[#18181B] placeholder-[#A1A1AA] bg-white transition-all duration-150 focus:outline-none"
                style={{
                  border: errors.password ? '1px solid #DC2626' : '1px solid #E4E4E7',
                }}
                onFocus={e => {
                  e.currentTarget.style.border = '1px solid #F97316';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249,115,22,0.1)';
                }}
                onBlur={e => {
                  e.currentTarget.style.border = errors.password ? '1px solid #DC2626' : '1px solid #E4E4E7';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {errors.password && (
                <p className="mt-1.5 text-xs text-[#DC2626] flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.password.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={login.isPending}
              className="w-full text-white font-bold py-2.5 px-4 rounded-lg transition-all duration-150 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              style={{ backgroundColor: login.isPending ? '#EA6C0A' : '#F97316' }}
              onMouseEnter={e => { if (!login.isPending) e.currentTarget.style.backgroundColor = '#EA6C0A'; }}
              onMouseLeave={e => { if (!login.isPending) e.currentTarget.style.backgroundColor = '#F97316'; }}
            >
              {login.isPending && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {login.isPending ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
